import { Injectable, inject } from '@angular/core';
import {
  CmsHeaderComponent,
  CmsPageState,
  ContentSlot,
  Page,
  PageType,
} from '@boels/models';
import { select, Store } from '@ngrx/store';
import { filter, lastValueFrom, Observable } from 'rxjs';
import { getPage, resetCmsPage } from '../actions/cms.actions';
import * as cmsSelectors from '../selectors/cms.selectors';
import { map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CmsFacade {
  private readonly store = inject(Store);

  isCmsRoute$: Observable<boolean> = this.store.select(
    cmsSelectors.selectIsCmsRoute,
  );
  activeLoaded$: Observable<Page | undefined> = this.store
    .select(cmsSelectors.selectCms)
    .pipe(
      filter(
        (cmsPageState) =>
          !cmsPageState.loading && (cmsPageState.error || cmsPageState.success),
      ),
      map((cmsPageState: CmsPageState) => cmsPageState.active),
    );

  // removes culture from url & remove the querystring
  public static normalize(route: string): string {
    if (!route) return '';
    route = route.replace(/^\/?[a-z]{2}-[a-z]{2}/, '');
    route = route.split('?')[0];
    // if route is empty store page as homepage
    route = route === '' ? 'homepage' : route;
    // Strip the hashtag from the route
    route = route.split('#')[0];
    return route;
  }

  loadPage(label: string, pageType: PageType): void {
    this.store.dispatch(
      getPage({ label: CmsFacade.normalize(label), pageType }),
    );
  }
  resetCmsPage(): void {
    this.store.dispatch(resetCmsPage());
  }

  getActivePage(): Observable<Page | undefined> {
    return this.store.pipe(select(cmsSelectors.selectActivePage));
  }

  getPreviousPage(): Promise<string | undefined> {
    return lastValueFrom(
      this.store.pipe(select(cmsSelectors.previousPage), take(1)),
    );
  }

  getHeaderData(): Observable<CmsHeaderComponent[] | undefined> {
    return this.store.select(cmsSelectors.selectActivePage).pipe(
      map((page) => {
        return page?.contentSlots?.contentSlot?.filter(
          (component: CmsHeaderComponent) =>
            component.slotId === 'SiteLogoSlot' ||
            component.slotId === 'SiteLoginSlot' ||
            component.slotId === 'NavigationBarSlot' ||
            component.slotId === 'MiniCartSlot' ||
            component.slotId === 'HeaderLinksSlot' ||
            component.slotId === 'BottomHeaderSlot' ||
            component.slotId === 'SiteContextSlot' ||
            component.slotId === 'SiteLinksSlot' ||
            component.slotId === 'SearchBoxSlot' ||
            component.slotId === 'PlaceholderContentSlot',
        );
      }),
    );
  }

  getFooterData(): Observable<ContentSlot | undefined> {
    return this.store.select(cmsSelectors.selectActivePage).pipe(
      map((a) => {
        return a?.contentSlots?.contentSlot?.find(
          (component) => component.slotId === 'FooterSlot',
        );
      }),
    );
  }
}
